<div class="inventory-container">

  <!-- ========================= HEADER ========================= -->

  <div class="inventory-header">

    <div class="title-section">

      <div class="title-icon">
        <i class="fa-solid fa-fire-burner"></i>
      </div>

      <div>
        <h1 class="inventory-title">Cooking Types</h1>

        <span class="products-count">
          {{ totalItems() }} Types
        </span>
      </div>

    </div>


    <div class="header-actions">

      <!-- Search -->
      <div class="search-wrapper">

        <i class="fa-solid fa-magnifying-glass search-icon"></i>

        <input
          type="text"
          placeholder="Search cooking types..."
          class="search-input"
          [value]="searchTerm()"
          (input)="onSearch($event)"
        />

        @if (searchTerm()) {
          <button
            type="button"
            class="clear-search"
            (click)="clearSearch()"
            title="Clear search">

            <i class="fa-solid fa-xmark"></i>

          </button>
        }

      </div>

      <!-- Add -->
      <button
        type="button"
        class="add-button"
        (click)="openCreate()">

        <i class="fa-solid fa-plus"></i>

        <span>Add Type</span>

      </button>

    </div>

  </div>


  <!-- ========================= FILTERS ========================= -->

  <div class="filters-card">

    <div class="filters-header">

      <div class="filters-title">
        <i class="fa-solid fa-filter"></i>
        <span>Filters</span>
      </div>

      @if (hasActiveFilters()) {
        <span class="active-filter-count">
          <i class="fa-solid fa-circle-check"></i>
          Filters Active
        </span>
      }

    </div>


    <div class="products-filters cooking-filters">

      <!-- Status -->
      <div class="filter-group">

        <label>
          <i class="fa-solid fa-toggle-on"></i>
          Status
        </label>

        <div class="select-wrapper">

          <select
            [value]="statusFilter()"
            (change)="onStatusFilterChange($event)"
            [disabled]="isLoading()">

            <option [value]="CookingTypeStatusFilter.All">
              All Statuses
            </option>

            <option [value]="CookingTypeStatusFilter.Active">
              Active Only
            </option>

            <option [value]="CookingTypeStatusFilter.Inactive">
              Inactive Only
            </option>

          </select>

          <i class="fa-solid fa-chevron-down"></i>

        </div>

      </div>


      <!-- Reset -->
      <button
        type="button"
        class="reset-filters-button"
        (click)="resetFilters()"
        [disabled]="isLoading() || !hasActiveFilters()">

        <i class="fa-solid fa-rotate-left"></i>
        <span>Reset</span>

      </button>

    </div>

  </div>


  <!-- ========================= ACTIVE FILTERS ========================= -->

  @if (hasActiveFilters()) {

    <div class="active-filters">

      <div class="active-filters-title">
        <i class="fa-solid fa-sliders"></i>
        Active Filters
      </div>

      @if (statusFilter() !== CookingTypeStatusFilter.All) {
        <span class="filter-badge status-filter">
          <i class="fa-solid fa-toggle-on"></i>
          {{ statusFilter() === CookingTypeStatusFilter.Active ? 'Active Only' : 'Inactive Only' }}
        </span>
      }

      @if (searchTerm()) {
        <span class="filter-badge search-filter">
          <i class="fa-solid fa-magnifying-glass"></i>
          "{{ searchTerm() }}"
        </span>
      }

    </div>

  }


  <!-- ========================= ERROR ========================= -->

  @if (errorMsg()) {

    <div class="error-banner">

      <i class="fa-solid fa-triangle-exclamation"></i>

      <span>{{ errorMsg() }}</span>

    </div>

  }


  <!-- ========================= TABLE ========================= -->

  <div class="products-table">

    <!-- Header -->
    <div class="table-header cooking-table-row">

      <div class="header-cell">
        <i class="fa-solid fa-hashtag"></i>
        #
      </div>

      <div class="header-cell">
        <i class="fa-solid fa-language"></i>
        Name (Arabic)
      </div>

      <div class="header-cell">
        <i class="fa-solid fa-language"></i>
        Name (English)
      </div>

      <div class="header-cell">
        <i class="fa-solid fa-toggle-on"></i>
        Status
      </div>

      <div class="header-cell">
        Actions
      </div>

    </div>


    <!-- Body -->
    <div class="table-body">

      @for (item of pagedItems(); track item.id) {

        <div class="product-row cooking-table-row">

          <!-- ID -->
          <div class="cell id-cell">
            <span class="product-id-badge">#{{ item.id }}</span>
          </div>


          <!-- Name AR -->
          <div class="cell name-cell">
            <div class="product-name" [title]="item.name_ar">
              {{ item.name_ar }}
            </div>
          </div>


          <!-- Name EN -->
          <div class="cell name-cell">
            <div class="product-name" [title]="item.name_en" dir="ltr">
              {{ item.name_en }}
            </div>
          </div>


          <!-- Status -->
          <div class="cell status-cell">

            <div class="status-row">

              <label class="switch-container">

                <input
                  type="checkbox"
                  [checked]="item.status === 1"
                  (change)="toggleStatus(item)"
                  class="toggle-switch"
                />

                <span class="slider"></span>

              </label>

              <span
                class="status-label"
                [class.active]="item.status === 1">

                {{ item.status === 1 ? 'Active' : 'Inactive' }}

              </span>

            </div>

          </div>


          <!-- Actions -->
          <div class="cell actions-cell">

            <div class="action-buttons">

              <button
                type="button"
                class="btn-edit"
                (click)="openEdit(item)"
                title="Edit">

                <i class="fa-solid fa-pen"></i>

              </button>

              <button
                type="button"
                class="btn-delete"
                (click)="askDelete(item)"
                title="Delete">

                <i class="fa-solid fa-trash"></i>

              </button>

            </div>

          </div>

        </div>

      } @empty {

        <div class="no-products">

          <div class="empty-icon">
            <i class="fa-solid fa-fire-burner"></i>
          </div>

          <div class="empty-title">
            No Cooking Types Found
          </div>

          <div class="empty-description">
            @if (hasActiveFilters()) {
              Try changing your filters or search term.
            } @else {
              Click "Add Type" to create your first one.
            }
          </div>

        </div>

      }

    </div>

  </div>


  <!-- ========================= PAGINATION ========================= -->

  @if (totalItems() > 0) {

    <div class="pagination-controls">

      <div class="pagination-info">

        Showing
        <strong>{{ startIndex() }}</strong>
        to
        <strong>{{ endIndex() }}</strong>
        of
        <strong>{{ totalItems() }}</strong>
        entries

      </div>


      <div class="pagination-buttons">

        <button
          type="button"
          (click)="previousPage()"
          [disabled]="currentPage() === 1 || isLoading()"
          class="pagination-btn">

          <i class="fa-solid fa-chevron-left"></i>
          Previous

        </button>


        <div class="page-numbers">

          @for (page of getPageNumbers(); track page) {

            <button
              type="button"
              (click)="goToPage(page)"
              [class.active]="page === currentPage()"
              [disabled]="isLoading()"
              class="page-btn">

              {{ page }}

            </button>

          }

        </div>


        <button
          type="button"
          (click)="nextPage()"
          [disabled]="currentPage() === totalPages() || isLoading()"
          class="pagination-btn">

          Next
          <i class="fa-solid fa-chevron-right"></i>

        </button>

      </div>


      <div class="items-per-page">

        <label>Items per page:</label>

        <select
          [value]="itemsPerPage()"
          (change)="onItemsPerPageChange($event)"
          [disabled]="isLoading()">

          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>

        </select>

      </div>

    </div>

  }


  <!-- ========================= LOADING ========================= -->

  @if (isLoading()) {

    <div class="loading-indicator">

      <div class="spinner"></div>

      <span>Loading cooking types...</span>

    </div>

  }

</div>


<!-- ========================= ADD / EDIT MODAL ========================= -->

@if (showFormModal()) {

  <div
    class="confirm-modal-overlay"
    (click)="closeFormModal()">

    <div
      class="confirm-modal form-modal"
      (click)="$event.stopPropagation()">

      <!-- Header -->
      <div class="confirm-modal-header">

        <div
          class="confirm-modal-icon"
          [class.info]="editingId() === null"
          [class.warning]="editingId() !== null">

          <i
            class="fa-solid"
            [class.fa-plus]="editingId() === null"
            [class.fa-pen]="editingId() !== null">
          </i>

        </div>

        <div>

          <h3 class="confirm-modal-title">

            {{ editingId() === null ? 'Add Cooking Type' : 'Edit Cooking Type' }}

          </h3>

          <p class="confirm-modal-subtitle">

            {{ editingId() === null
                ? 'Create a new cooking type.'
                : 'Update the cooking type details.' }}

          </p>

        </div>

      </div>


      <!-- Form -->
      <form
        [formGroup]="form"
        (ngSubmit)="save()"
        class="form-body">

        <!-- Name AR -->
        <div class="form-field">

          <label class="form-label">
            Name (Arabic) <span class="required">*</span>
          </label>

          <input
            type="text"
            class="form-input"
            formControlName="name_ar"
            placeholder="مثال: مشوي"
            [class.invalid]="nameArControl?.touched && nameArControl?.invalid"
          />

          @if (nameArControl?.touched && nameArControl?.invalid) {
            <small class="form-error">
              Arabic name is required (max 150 chars)
            </small>
          }

        </div>


        <!-- Name EN -->
        <div class="form-field">

          <label class="form-label">
            Name (English) <span class="required">*</span>
          </label>

          <input
            type="text"
            class="form-input"
            formControlName="name_en"
            placeholder="e.g. Grilled"
            dir="ltr"
            [class.invalid]="nameEnControl?.touched && nameEnControl?.invalid"
          />

          @if (nameEnControl?.touched && nameEnControl?.invalid) {
            <small class="form-error">
              English name is required (max 150 chars)
            </small>
          }

        </div>


        <!-- Status -->
        <div class="form-field">

          <label class="form-label">Status</label>

          <div class="select-wrapper">

            <select
              class="form-input"
              formControlName="status">

              <option [ngValue]="1">Active</option>
              <option [ngValue]="0">Inactive</option>

            </select>

            <i class="fa-solid fa-chevron-down"></i>

          </div>

        </div>


        <!-- Actions -->
        <div class="confirm-modal-actions">

          <button
            type="button"
            class="confirm-modal-btn cancel"
            (click)="closeFormModal()"
            [disabled]="saving()">

            <i class="fa-solid fa-xmark"></i>
            Cancel

          </button>

          <button
            type="submit"
            class="confirm-modal-btn confirm primary"
            [disabled]="saving() || form.invalid">

            @if (saving()) {
              <span class="spinner-small"></span>
              Saving...
            } @else {
              <i class="fa-solid fa-check"></i>
              {{ editingId() === null ? 'Create' : 'Save Changes' }}
            }

          </button>

        </div>

      </form>

    </div>

  </div>

}


<!-- ========================= DELETE CONFIRMATION MODAL ========================= -->

@if (showDeleteConfirm()) {

  <div
    class="confirm-modal-overlay"
    (click)="closeDeleteConfirm()">

    <div
      class="confirm-modal"
      (click)="$event.stopPropagation()">

      <!-- Header -->
      <div class="confirm-modal-header">

        <div class="confirm-modal-icon danger">
          <i class="fa-solid fa-trash"></i>
        </div>

        <div>

          <h3 class="confirm-modal-title">Delete Cooking Type?</h3>

          <p class="confirm-modal-subtitle">
            This action cannot be undone.
          </p>

        </div>

      </div>


      <!-- Product preview -->
      @if (pendingDelete(); as item) {

        <div class="confirm-modal-product">

          <div class="confirm-modal-product-info">

            <span class="confirm-modal-product-name">
              {{ item.name_ar }} — {{ item.name_en }}
            </span>

            <span class="confirm-modal-product-id">
              #{{ item.id }}
            </span>

          </div>

        </div>

      }


      <!-- Warning -->
      <div class="confirm-modal-note">

        <i class="fa-solid fa-circle-info"></i>

        <div>

          <strong>Heads up:</strong>

          <p>
            If this cooking type is used in any product,
            the delete will be rejected by the server.
            You must first remove it from all products.
          </p>

        </div>

      </div>


      <!-- Actions -->
      <div class="confirm-modal-actions">

        <button
          type="button"
          class="confirm-modal-btn cancel"
          (click)="closeDeleteConfirm()">

          <i class="fa-solid fa-xmark"></i>
          Cancel

        </button>

        <button
          type="button"
          class="confirm-modal-btn confirm"
          (click)="confirmDelete()">

          <i class="fa-solid fa-trash"></i>
          Yes, Delete

        </button>

      </div>

    </div>

  </div>

}